'use client';
import {
  type ProductionPeriod,
  type ChangeCalculationParams,
  type ProductionReportMeasurementInput,
  type TagValueData,
} from '../../utils/types';
import type React from 'react';
import Drawer from '@/components/Drawer';
import CalculationList from './calculation-list';
import { Button, Caption, Separator } from '@yaradigitallabs/ahua-react';
import { Colors } from '../../../../../public/GlobalTokens';
import { useEffect, useState } from 'react';
import { CalculationDetailCard } from '../../components/calculation-detail-card';
import { fetchTagValues } from '../../api/fetch-tag-values.api';
import {
  useCalculationContext,
  initialMeasurement,
} from '../../context/calculation-context';
import type { MaterialWithSoftTag } from '@/features/match-maker/utils/match-maker.types';
import { fetchDefaultUnitFromMaterial } from '../../utils/helpers';

type CalculationSelectionDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  calculation: ChangeCalculationParams | null;
  productionPeriod?: ProductionPeriod;
  setCalculation: React.Dispatch<
    React.SetStateAction<ChangeCalculationParams | null>
  >;
  onConfirm: (measurement: ProductionReportMeasurementInput) => void;
};

export const CalculationSelectionDrawer: React.FC<
  CalculationSelectionDrawerProps
> = ({ isOpen, onClose, calculation, productionPeriod, onConfirm }) => {
  const [selectionDetail, setSelectionDetail] =
    useState<MaterialWithSoftTag | null>(null);
  const [tagValues, setTagValues] = useState<Record<string, TagValueData>>({});
  const [isTagValuesLoading, setIsTagValuesLoading] = useState(false);
  const [tagValuesError, setTagValuesError] = useState<string | null>(null);

  const { measurement, setMeasurement, materialUnits } =
    useCalculationContext();

  const handleClose = () => {
    onClose();
  };

  const handleConfirm = () => {
    onConfirm(measurement);
    setMeasurement(initialMeasurement);
    onClose();
  };

  useEffect(() => {
    if (!calculation?.material) return;
    const materialTags = calculation?.material?.tags ?? [];
    if (!isOpen || materialTags.length === 0 || !productionPeriod) {
      setTagValues({});
      setTagValuesError(null);
      setIsTagValuesLoading(false);
      return;
    }

    const period_start = productionPeriod.start_time;
    const period_end = productionPeriod.end_time;
    let isMounted = true;

    const loadTagValues = async () => {
      setIsTagValuesLoading(true);
      setTagValuesError(null);

      const tagIds = materialTags.map(tag => tag.id);
      const tagTimeSeriesMap = materialTags.reduce(
        (acc, cur) => {
          if (!calculation.material) return acc;
          const unit = fetchDefaultUnitFromMaterial(
            calculation.material,
            materialUnits
          );
          acc[cur.timeseries_id] = {
            ...cur,
            unit: cur.unit || unit || '',
          };
          return acc;
        },
        {} as Record<string, MaterialWithSoftTag>
      );

      let tagValues: Array<{ tagId: string; data: TagValueData }> = [];

      try {
        const tagValuesResponse = await fetchTagValues({
          tag_ids: tagIds,
          period_start,
          period_end,
          latest_value: true,
        });
        tagValues = tagValuesResponse.successes.map(item => ({
          tagId: tagTimeSeriesMap[item.id].id,
          data: {
            value: item.data_points[0]?.value ?? 0,
            unit: item.unit || tagTimeSeriesMap[item.id].unit || '',
            timestamp: item.data_points[0]?.timestamp ?? '',
            hasError: (tagValuesResponse.errors || []).length > 0,
          },
        }));
      } catch (error) {
        console.error('Failed to fetch tag values:', error);
        tagValues = materialTags.map(tag => ({
          tagId: tag.id,
          data: {
            value: 0,
            unit: tag.unit,
            timestamp: '',
            hasError: true,
          },
        }));
      }

      if (!isMounted) {
        return;
      }

      const nextTagValues = Object.fromEntries(
        tagValues.map(({ tagId, data }) => [tagId, data])
      ) as Record<string, TagValueData>;
      const hasError = tagValues.some(({ data }) => data.hasError);

      setTagValues(nextTagValues);
      setTagValuesError(
        hasError ? 'Some tag values could not be loaded.' : null
      );
      setIsTagValuesLoading(false);
    };

    void loadTagValues();

    return () => {
      isMounted = false;
    };
  }, [calculation?.material, isOpen, productionPeriod, materialUnits]);

  return (
    <Drawer
      isOpen={isOpen}
      {...(selectionDetail
        ? {
            onBack: () => setSelectionDetail(null),
          }
        : {
            footer: (
              <Button
                css={{ width: '100%' }}
                colorConcept="brand"
                size="s"
                onClick={handleConfirm}
              >
                Confirm
              </Button>
            ),
          })}
      onClose={handleClose}
      title={
        selectionDetail
          ? 'Calculation method details'
          : 'Change calculation method'
      }
      widthClass="w-[500px]"
    >
      {selectionDetail ? (
        <CalculationDetailCard tag={selectionDetail} />
      ) : (
        <div className="border border-gray-200 rounded-md">
          <div className="p-4">
            <Caption css={{ color: Colors.colorType.grayscale.contrast }}>
              Select one calculation method or enter a custom value
            </Caption>
          </div>
          <Separator />
          <CalculationList
            isLoading={isTagValuesLoading}
            error={tagValuesError}
            tagValues={tagValues}
            material={calculation?.material ?? null}
            onDetailClick={setSelectionDetail}
          />
        </div>
      )}
    </Drawer>
  );
};
